import { ICONS, IIconInfo } from './marker-icons';

export const EARTH_RADIUS_METERS: number = 6378137;

export const MINIMUM_FENCE_AREA_SQ_METERS: number = 500;
export const MINIMUM_FENCE_AREA_SQ_FEET: number = 5500;
export const WARNING_THRESHOLD_FENCE_AREA_SQ_METERS: number = 1000;
export const WARNING_THRESHOLD_FENCE_AREA_SQ_FEET: number = 11000;
export const DEFAULT_GEOFENCE_RADIUS_METERS: number = 250;
export const MAXIMUM_FENCE_AREA_SQ_METERS: number = 1300000000000; // equivilent to 1,300,000 km squared
export const MAXIMUM_FENCE_AREA_SQ_KM: number = MAXIMUM_FENCE_AREA_SQ_METERS / 1000000;
export const MAXIMUM_FENCE_AREA_SQ_MILE: number = 500000;

export const DEFAULT_CIRCLE_SIMULATION_NUM_POINTS: number = 32;
export const MINIMUM_CIRCLE_RADIUS_METERS: number = 20;
export const MINIMUM_CIRCLE_RADIUS_MILE: number = 0.01;
export const DEFAULT_CIRCLE_GEOFENCE_RADIUS_METERS: number = DEFAULT_GEOFENCE_RADIUS_METERS;
export const MAXIMUM_CIRCLE_RADIUS_METERS: number = 640000;
export const MAXIMUM_CIRCLE_RADIUS_MILE: number = 400;

export const MINIMUM_MARKER_DISTANCE: number = 3;

export interface IGeneratedImageShape {
  coords: number[];
  type: string;
}

export interface IGeneratedImageAnchor {
  x: number;
  y: number;
}

export interface IGeneratedImage {
  image: string;
  anchor: null | IGeneratedImageAnchor;
  shape?: IGeneratedImageShape;
}

export interface ILine {
  start: google.maps.LatLng;
  end: google.maps.LatLng;
  isOverlapped: boolean;
}

interface IPoint {
  x: number;
  y: number;
}

export interface IGeofenceError {
  type: string;
  message: string;
  note?: string;
  shortMessage?: string;
}

export interface IGeofenceChangeData {
  polygonPath: google.maps.LatLng[];
  radius?: number;
  center?: google.maps.LatLng;
  scrollView: boolean;
}

export interface ICircleData {
  radius: number;
  center: google.maps.LatLng;
}

export function convertDDToDMS(D, lng = false) {
  const dir = D < 0 ? lng ? 'W' : 'S' : lng ? 'E' : 'N';
  const degree = 0 | (D < 0 ? D = -D : D);
  const min = 0 | D % 1 * 60;
  const sec = (0 | D * 60 % 1 * 6000) / 100;
  return `${degree}°${min}'${sec}\"${dir}`;
}

export function findMarkerPolylineOverlapping(
  point: google.maps.LatLng,
  points: google.maps.LatLng[],
  map: google.maps.Map,
): boolean {
  if (getDistance(point, points[0]) <= MINIMUM_MARKER_DISTANCE) return false;
  else {
    let isOverlapped: boolean = false;
    const line: google.maps.Polyline = new google.maps.Polyline({
      strokeColor: 'transparent',
      strokeWeight: 6,
      map,
    });
    for (let i = 0; i < points.length - 1; i++) {
      line.setPath([points[i], points[i + 1]]);
      if (!point.equals(points[i + 1]) && !point.equals(points[i])) {
        isOverlapped = google.maps.geometry.poly.isLocationOnEdge(point, line, 0.000021);
        if (isOverlapped) break;
      }
    }
    line.setMap(null);
    return isOverlapped;
  }
}

export function findOverlappingMarkers(marker: google.maps.Marker, markers: google.maps.Marker[], ignoreFirst: boolean): boolean {
  const startIndex: number = ignoreFirst ? 1 : 0;
  let isOverlapped: boolean = false;
  for (let i = startIndex; i < markers.length; i++) {
    if (markers[i] != marker && getDistance(marker.getPosition(), markers[i].getPosition()) < MINIMUM_MARKER_DISTANCE) {
      isOverlapped = true;
    }
  }
  return isOverlapped;
}

export function validateMinimumGeofenceSize(markers: google.maps.Marker[]): boolean {
  const polygonArea: number = google.maps.geometry.spherical.computeArea(markers.map(marker => marker.getPosition()));
  if (polygonArea >= MINIMUM_FENCE_AREA_SQ_METERS) return true;
  return false;
}

export function validateMaximumGeofenceSize(markers: google.maps.Marker[]): boolean {
  const polygonArea: number = google.maps.geometry.spherical.computeArea(markers.map(marker => marker.getPosition()));
  if (polygonArea <= MAXIMUM_FENCE_AREA_SQ_METERS) return true;
  return false;
}

export function validateAddressInsideGeofence(addressLatLng: google.maps.LatLng, polygon: google.maps.Polygon): boolean {
  return google.maps.geometry.poly.containsLocation(addressLatLng, polygon);
}

export function findOverlappingLines(
  point: google.maps.LatLng,
  markers: google.maps.Marker[],
  ignoreFirstLine: boolean = false,
  isPolygonVisible: boolean = false,
): boolean {
  if (markers && markers.length >= 2) {
    const lines = getConnectedLinesToPoint(point, markers, isPolygonVisible);
    lines.forEach(line => {
      line.isOverlapped = doLineOverlap(line, markers, ignoreFirstLine, isPolygonVisible);
    });

    if (lines.find(line => line.isOverlapped)) return true;
    else return false;
  } else {
    return false;
  }
}

export function getConnectedLinesToPoint(
  point: google.maps.LatLng,
  markers: google.maps.Marker[],
  isPolygonVisible: boolean = false,
): ILine[] {
  const lines: ILine[] = [];
  let pointIndex: number;
  if (markers && markers.length > 0) {
    pointIndex = markers.findIndex(marker => marker.getPosition() == point);
    if (pointIndex > 0) {
      lines.push({
        start: markers[pointIndex - 1].getPosition(),
        end: markers[pointIndex].getPosition(),
        isOverlapped: false,
      });

      if (pointIndex != markers.length - 1) {
        lines.push({
          start: markers[pointIndex].getPosition(),
          end: markers[pointIndex + 1].getPosition(),
          isOverlapped: false,
        });
      } else if (isPolygonVisible) {
        lines.push({
          start: markers[pointIndex].getPosition(),
          end: markers[0].getPosition(),
          isOverlapped: false,
        });
      }
    } else {
      lines.push({
        start: markers[markers.length - 1].getPosition(),
        end: point,
        isOverlapped: false,
      });
      if (isPolygonVisible) {
        lines.push({
          start: point,
          end: markers[1].getPosition(),
          isOverlapped: false,
        });
      }
    }
  }
  return lines;
}

function doLineOverlap(line: ILine, markers: google.maps.Marker[], ignoreFirstLine: boolean, isPolygonVisible: boolean): boolean {
  const polylinePoints: google.maps.LatLng[] = markers.map(marker => marker.getPosition());
  const index: number = ignoreFirstLine ? 1 : 0;
  let nextPointIndex: number;
  const p1: IPoint = {
    x: line.start.lat(),
    y: line.start.lng(),
  };

  const p2: IPoint = {
    x: line.end.lat(),
    y: line.end.lng(),
  };
  for (let i = index; i < polylinePoints.length; i++) {
    nextPointIndex = i + 1;
    const q1: IPoint = {
      x: polylinePoints[i].lat(),
      y: polylinePoints[i].lng(),
    };

    if (i == polylinePoints.length - 1) {
      if (isPolygonVisible) nextPointIndex = 0;
      else return false;
    }

    const q2: IPoint = {
      x: polylinePoints[nextPointIndex].lat(),
      y: polylinePoints[nextPointIndex].lng(),
    };

    if ((getDistance(line.start, polylinePoints[i]) == 0) || (getDistance(line.start, polylinePoints[nextPointIndex]) == 0)
      || (getDistance(line.end, polylinePoints[i]) == 0)
      || (getDistance(line.start, polylinePoints[nextPointIndex]) == 0)) {
      // same lines found., ignore
    } else {
      if (doLineSegmentsIntersect(p1, p2, q1, q2)) {
        return true;
      }
    }
  }
}

export function doLineSegmentsIntersect(p: IPoint, p2: IPoint, q: IPoint, q2: IPoint): boolean {
  const r: IPoint = subtractPoints(p2, p);
  const s: IPoint = subtractPoints(q2, q);

  const uNumerator: number = crossProduct(subtractPoints(q, p), r);
  const denominator: number = crossProduct(r, s);

  if (uNumerator == 0 && denominator == 0) {
    // They are coLlinear

    // Do they touch? (Are any of the points equal?)
    if (equalPoints(p, q) || equalPoints(p, q2) || equalPoints(p2, q) || equalPoints(p2, q2)) {
      return true;
    }
    // Do they overlap? (Are all the point differences in either direction the same sign)
    return !allEqual((q.x - p.x < 0), (q.x - p2.x < 0), (q2.x - p.x < 0), (q2.x - p2.x < 0))
      || !allEqual((q.y - p.y < 0), (q.y - p2.y < 0), (q2.y - p.y < 0), (q2.y - p2.y < 0));
  }

  if (denominator == 0) {
    // lines are parallel
    return false;
  }

  const u: number = uNumerator / denominator;
  const t: number = crossProduct(subtractPoints(q, p), s) / denominator;

  return (t >= 0) && (t <= 1) && (u >= 0) && (u <= 1);
}

export function getDistance(p1: google.maps.LatLng, p2: google.maps.LatLng): number {
  const dLat: number = rad(p2.lat() - p1.lat());
  const dLong: number = rad(p2.lng() - p1.lng());
  const a: number = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(p1.lat())) * Math.cos(rad(p2.lat())) *
    Math.sin(dLong / 2) * Math.sin(dLong / 2);
  const c: number = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d: number = EARTH_RADIUS_METERS * c;
  return d;
}

function rad(x: number): number {
  return x * Math.PI / 180;
}

function deg(x: number): number {
  return x * 180 / Math.PI;
}

function crossProduct(point1, point2): number {
  return point1.x * point2.y - point1.y * point2.x;
}

function subtractPoints(point1: IPoint, point2: IPoint): IPoint {
  const result: IPoint = {
    x: point1.x - point2.x,
    y: point1.y - point2.y,
  };

  return result;
}

function equalPoints(point1: IPoint, point2: IPoint): boolean {
  return (point1.x == point2.x) && (point1.y == point2.y);
}

function allEqual(...args): boolean {
  const firstValue = arguments[0];
  for (let i = 1; i < arguments.length; i += 1) {
    if (arguments[i] != firstValue) {
      return false;
    }
  }
  return true;
}

export function validatePolygonToPointOverlap(point: google.maps.LatLng, polygons: google.maps.Polygon[]): boolean {
  for (const polygon of polygons) {
    if (google.maps.geometry.poly.containsLocation(point, polygon)) return true;
  }
}

export function validatePolygonToPolylineOverlap(
  point: google.maps.LatLng,
  markers: google.maps.Marker[],
  polygons: google.maps.Polygon[],
): boolean {
  if (markers && markers.length > 0) {
    const lines: ILine[] = getConnectedLinesToPoint(point, markers);
    for (const line of lines) {
      for (const polygon of polygons) {
        if (checkLineToPolygonIntersect(line, polygon)) {
          break;
        }
      }

      if (line.isOverlapped) break;
    }

    return lines.some(line => line.isOverlapped);
  }
  return false;
}

function checkLineToPolygonIntersect(line: ILine, polygon: google.maps.Polygon): boolean {
  const p1: IPoint = {
    x: line.start.lat(),
    y: line.start.lng(),
  };

  const p2: IPoint = {
    x: line.end.lat(),
    y: line.end.lng(),
  };

  const polygonPath: IPoint[] = polygon.getPath().getArray()
    .map((point: google.maps.LatLng) => {
      return {
        x: point.lat(),
        y: point.lng(),
      };
    })
  ;
  for (let i = 0; i < polygonPath.length - 1; i++) {
    const q1: IPoint = {
      x: polygonPath[i].x,
      y: polygonPath[i].y,
    };

    const q2: IPoint = {
      x: polygonPath[i + 1].x,
      y: polygonPath[i + 1].y,
    };
    if (doLineSegmentsIntersect(p1, p2, q1, q2)) {
      line.isOverlapped = true;
      break;
    }
  }

  return line.isOverlapped;
}

function validateIfPolygonIsContained(point: google.maps.LatLng, polygons: google.maps.Polygon[]) {
  return !polygons.every(polygon => !google.maps.geometry.poly.containsLocation(point, polygon));
}

export function validateIfNewPolygonContainsOlderPolygon(
  markers: google.maps.Marker[],
  polygons: google.maps.Polygon[],
): boolean {
  const newPolygon: google.maps.Polygon = new google.maps.Polygon({
    paths: [markers.map((m: google.maps.Marker) => m.getPosition())],
    visible: false,
  });
  let isContained: boolean = false;

  for (const polygon of polygons) {
    if (google.maps.geometry.poly.containsLocation(polygon.getPath().getArray()[0], newPolygon)) {
      isContained = true;
      break;
    }
  }
  return isContained;
}

export function getCentroidForGeofence(pts: google.maps.LatLng[]): google.maps.LatLng {
  const first: google.maps.LatLng = pts[0];
  const last: google.maps.LatLng = pts[pts.length - 1];
  if (first.lat() != last.lat() || first.lng() != last.lng()) pts.push(first);
  let twicearea: number = 0;
  let x: number = 0;
  let y: number = 0;
  const nPts: number = pts.length;
  let p1: google.maps.LatLng;
  let p2: google.maps.LatLng;
  let f: number = 0;
  for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
    p1 = pts[i]; p2 = pts[j];
    f = (p1.lng() - first.lng()) * (p2.lat() - first.lat()) - (p2.lng() - first.lng()) * (p1.lat() - first.lat());
    twicearea += f;
    x += (p1.lat() + p2.lat() - 2 * first.lat()) * f;
    y += (p1.lng() + p2.lng() - 2 * first.lng()) * f;
  }
  f = twicearea * 3;
  return new google.maps.LatLng(x / f + first.lat(), y / f + first.lng());
}

export function convertLatlngToPoint(latLng: google.maps.LatLng, map: google.maps.Map): google.maps.Point {
  const topRight: google.maps.Point = map.getProjection().fromLatLngToPoint(map.getBounds().getNorthEast());
  const bottomLeft: google.maps.Point = map.getProjection().fromLatLngToPoint(map.getBounds().getSouthWest());
  const scale: number = Math.pow(2, map.getZoom());
  const worldPoint: google.maps.Point = map.getProjection().fromLatLngToPoint(latLng);
  return new google.maps.Point((worldPoint.x - bottomLeft.x) * scale, (worldPoint.y - topRight.y) * scale);
}

export function convertCircleToPath(
  circle: google.maps.Circle,
  numPoints: number = DEFAULT_CIRCLE_SIMULATION_NUM_POINTS,
): google.maps.LatLng[] {
  const radius: number = circle.getRadius();
  const center: google.maps.LatLng = circle.getCenter();

  const path: google.maps.LatLng[] = [];
  for (let i = 0; i < numPoints; i++) {
    path.push(google.maps.geometry.spherical.computeOffset(center, radius, i * 360 / numPoints));
  }
  path.push(path[0]);
  return path;
}

export function validatePolygonsOverlapping(markers: google.maps.Marker[], polygons: google.maps.Polygon[]): boolean {
  let isOverlappingPolygons: boolean = false;
  for (const marker of markers) {
    if (
      validatePolygonToPolylineOverlap(marker.getPosition(), markers, polygons) ||
      validateIfPolygonIsContained(marker.getPosition(), polygons)
    ) {
      isOverlappingPolygons = true;
      break;
    }
  }
  return isOverlappingPolygons;
}

function preloadImage(url: string, imageWidth: number, imageHeight: number): Promise<HTMLImageElement> {
  const image: HTMLImageElement = new Image(imageWidth, imageHeight);
  return new Promise(resolve => {
    image.addEventListener('load', () => {
      resolve(image);
    }, { once: true });
    image.src = url;
  });
}

const imagesForPreloading: string[] = [
  'marker-driving-eld-enabled-glow',
  'marker-driving-eld-enabled',
  'vehicle-stationary',
  'off-duty',
  'driver-on-duty',
  'driving-without-eld',
  'vehicle-error',
  'selected-shipper-facility',
  'shipper-facility',
];

export const CANVAS_WIDTH: number = 120;
export const CANVAS_HEIGHT: number = 55;
const preloadedImages: { [key: string]: Promise<HTMLImageElement> } = {};

function preloadAndStoreImages(): Promise<HTMLImageElement[]> {
  const imageLoadingPromises: Array<Promise<HTMLImageElement>> = [];
  imagesForPreloading.forEach((imageName: string) => {
    const imageInfo: { 'url': string } = ICONS[imageName];
    // tslint:disable-next-line:no-use-before-declare
    const imageLoadingPromise: Promise<HTMLImageElement> = preloadImage(imageInfo.url, CANVAS_WIDTH, CANVAS_HEIGHT);
    preloadedImages[imageName] = imageLoadingPromise;
    imageLoadingPromises.push(imageLoadingPromise);
  });

  return Promise.all(imageLoadingPromises);
}

preloadAndStoreImages();

const devicePixelRatio = window.devicePixelRatio;
const MAX_TITLE_LENGTH: number = 18;
const FONT_SIZE: number = 11;
const ALERT_IMAGE_WIDTH = 16 * devicePixelRatio;
const ALERT_IMAGE_HEIGHT = 16 * devicePixelRatio;
const ALERT_IMAGE_Y = 25 * devicePixelRatio;
const ALERT_IMAGE_X_OFFSET = 30 * devicePixelRatio;
const DUTY_STATUS_ICON_X = 56 * devicePixelRatio;
const DUTY_STATUS_ICON_Y = 13 * devicePixelRatio;
const DUTY_STATUS_ICON_X_ROTATED = 60 * devicePixelRatio;
const DUTY_STATUS_ICON_Y_ROTATED = 20 * devicePixelRatio;
const DUTY_STATUS_ICON_WIDTH = 16 * devicePixelRatio;
const DUTY_STATUS_ICON_HEIGHT = 16 * devicePixelRatio;
const DUTY_STATUS_ICON_WIDTH_ROTATED = 20 * devicePixelRatio;
const DUTY_STATUS_ICON_HEIGHT_ROTATED = 20 * devicePixelRatio;
const SHIPPER_FACILITY_ICON_WIDTH = 48 * devicePixelRatio;
const SHIPPER_FACILITY_ICON_HEIGHT = 48 * devicePixelRatio;
const CAPTION_X_FACTOR = 2.5 * devicePixelRatio;
const CAPTION_Y_FACTOR = 19 * devicePixelRatio;
const ROUNDED_RECT_X_OFFSET = 2.5 * devicePixelRatio;
const ROUNDED_RECT_Y_OFFSET = 5 * devicePixelRatio;
const ROUNDED_RECT_WIDTH_FACTOR = 10 * devicePixelRatio;
const ROUNDED_RECT_HEIGHT_FACTOR = 20 * devicePixelRatio;
const ROUNDED_RECT_RADIUS_FACTOR = 4;

function formatTitle(title: string) {
  let formatted = title;
  if (title.length > MAX_TITLE_LENGTH) {
    formatted = title.substring(0, MAX_TITLE_LENGTH - 3);
    formatted += '...';
  }
  return formatted;
}

const width = CANVAS_WIDTH;
const height = CANVAS_HEIGHT;
const canvas = document.createElement('canvas');
canvas.width = width * devicePixelRatio;
canvas.height = height * devicePixelRatio;
canvas.style.width = `${CANVAS_WIDTH}px`;
canvas.style.height = `${CANVAS_HEIGHT}px`;
const context = canvas.getContext('2d');
context.fillStyle = 'rgba(255, 255, 255, 0)';
context.font = `${FONT_SIZE * devicePixelRatio}px 'Inter'`;
const CAPTION_WIDTH_WITH_FACTOR = ((width / 2) * devicePixelRatio) + CAPTION_X_FACTOR;
const CAPTION_HEIGHT_WITH_FACTOR = ((height / 2) * devicePixelRatio) + CAPTION_Y_FACTOR;
export function generateImage(
  title: string,
  imageName: string,
  showAlert: boolean = false,
  bearing: number = null,
): Promise<IGeneratedImage> {
  let formattedTitle = '';
  const { url, anchor, shape, shouldRotate }: IIconInfo = ICONS[imageName];

  const degrees = bearing != null ? bearing : 0;
  const writeCaption = title && title.length > 0;
  if (writeCaption) formattedTitle = formatTitle(title);

  const imagePromise: Promise<HTMLImageElement> =
    preloadedImages[imageName] || (preloadedImages[imageName] = preloadImage(url, CANVAS_WIDTH, CANVAS_HEIGHT));

  const errorImagePromise: Promise<HTMLImageElement> = preloadedImages['vehicle-error'] ||
    (preloadedImages['vehicle-error'] = preloadImage(ICONS['vehicle-error'].url, null, null));

  const rotateAndPaint = (
    canvasContext: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    imageWidth: number,
    imageHeight: number,
    rotation: number = 0,
  ): void => {
    canvasContext.save();
    canvasContext.translate(x - (imageWidth / 2), y - (imageHeight / 2));
    canvasContext.translate(imageWidth / 2, imageHeight / 2);
    canvasContext.rotate(rotation * (Math.PI / 180));
    canvasContext.drawImage(image, -imageWidth / 2, -imageHeight / 2, imageWidth, imageHeight);
    canvasContext.restore();
  };

  return Promise.all([imagePromise, errorImagePromise])
  .then(([markerIcon, errorImage]: [HTMLImageElement, HTMLImageElement]) => {
    context.save();
    if (shouldRotate) {
      rotateAndPaint(context, markerIcon,
        DUTY_STATUS_ICON_X_ROTATED,
        DUTY_STATUS_ICON_Y_ROTATED,
        DUTY_STATUS_ICON_WIDTH_ROTATED, DUTY_STATUS_ICON_HEIGHT_ROTATED, degrees,
      );
    } else if (imageName.indexOf('shipper-facility') > -1) {
      context.drawImage(markerIcon,
        DUTY_STATUS_ICON_X,
        DUTY_STATUS_ICON_Y,
        SHIPPER_FACILITY_ICON_HEIGHT,
        SHIPPER_FACILITY_ICON_WIDTH);
    } else {
      context.drawImage(markerIcon,
        DUTY_STATUS_ICON_X,
        DUTY_STATUS_ICON_Y,
        DUTY_STATUS_ICON_HEIGHT,
        DUTY_STATUS_ICON_WIDTH);
    }
    context.fillStyle = 'rgb(0, 0, 0)';
    context.textAlign = 'center';
    if (writeCaption) {
      roundRect(
        context,
        ((width / 2) * devicePixelRatio) - (context.measureText(formattedTitle).width / 2) -
        (ROUNDED_RECT_X_OFFSET),
        ((height / 2) * devicePixelRatio) + ROUNDED_RECT_Y_OFFSET,
        context.measureText(formattedTitle).width + ROUNDED_RECT_WIDTH_FACTOR,
        ROUNDED_RECT_HEIGHT_FACTOR,
        4 * devicePixelRatio,
        true,
      );
    }
    if (showAlert) {
      context.drawImage(
        errorImage,
        (context.measureText(formattedTitle).width / 2 + (width / 4) * devicePixelRatio) +
        ALERT_IMAGE_X_OFFSET,
        ALERT_IMAGE_Y,
        ALERT_IMAGE_WIDTH,
        ALERT_IMAGE_HEIGHT,
      );
    }
    if (writeCaption) {
      context.fillStyle = 'rgb(255, 255, 255)';
      context.fillText(
        formattedTitle,
        CAPTION_WIDTH_WITH_FACTOR,
        CAPTION_HEIGHT_WITH_FACTOR,
      );
    }
    context.scale(devicePixelRatio, devicePixelRatio);
    const base64Image = canvas.toDataURL('image/png');
    context.restore();
    context.clearRect(0, 0, canvas.width, canvas.height);
    return { image: base64Image, anchor, shape };
  });
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rectWidth: number,
  rectHeight: number,
  radius: number = 5,
  fill: boolean = false,
  stroke = true,
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + rectWidth - radius, y);
  ctx.quadraticCurveTo(x + rectWidth, y, x + rectWidth, y + radius);
  ctx.lineTo(x + rectWidth, y + rectHeight - radius);
  ctx.quadraticCurveTo(x + rectWidth, y + rectHeight, x + rectWidth - radius, y + rectHeight);
  ctx.lineTo(x + radius, y + rectHeight);
  ctx.quadraticCurveTo(x, y + rectHeight, x, y + rectHeight - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  if (stroke) ctx.stroke();
  if (fill) ctx.fill();
}
